import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Heading, Text, TextLink } from '@frontend/design-system';

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing(3)};
    padding: ${theme.spacing(2)};
    height: min-content;
    width: 100%;
    @media (min-width: ${breakpoints.medium.max}px) {
      padding: ${theme.spacing(5)};
    }
  `,
  cardContainer: css`
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    gap: ${theme.spacing(3)};
    padding: ${theme.spacing(2)};
    width: 100%;

    @media (min-width: ${breakpoints.medium.max}px) {
      flex-direction: row;
    }
  `,
  card: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2)};
    background: ${theme.colors.warning5};
    border: 1px solid ${theme.colors.warning20};
    border-radius: ${theme.borderRadius.medium};

    @media (min-width: ${breakpoints.medium.max}px) {
      max-width: 375px;
      height: 100%;
      width: 100%;
      aspect-ratio: 16/9;
    }
  `,
  quote: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  video: css`
    width: 100%;
    height: 100%;
    max-width: 375px;
    aspect-ratio: 16/9;
    border: 1px solid ${theme.colors.neutral5};
    border-radius: ${theme.borderRadius.medium};
  `,
};

const urls = {
  florenceFamilyDentistry: 'https://www.getweave.com/case-studies/florence-family-dentistry/',
  allCaseStudies: 'https://www.getweave.com/case-studies/',
  youtubeVideo: 'https://weave.wistia.com/medias/72cp4yis4q',
};

export const StripeCaseStudies = () => {
  const { t } = useTranslation('payments');

  const onTextLinkClick = (link: string) => {
    window.open(link, '_blank');
  };

  return (
    <div css={styles.container} data-testid='stripe-case-studies'>
      <Heading level={2} textAlign='center'>
        {t('Users Like You Are Already Winning')}
      </Heading>
      <div css={styles.cardContainer}>
        <div css={styles.card}>
          <span css={styles.quote}>
            <Text>{t(`“In the office, it has been the best return on investment that we have made.”`)}</Text>
            <Text weight='bold' size='medium'>
              - {t('Florence Family Dentistry')}
            </Text>
          </span>
          <TextLink
            onClick={() => onTextLinkClick(urls.florenceFamilyDentistry)}
            weight='bold'
            trackingId='stripe-signup-full-case-study'
          >
            {t('Full Case Study')}
          </TextLink>
        </div>
        <iframe
          css={styles.video}
          title='Weave Payments'
          src={urls.youtubeVideo}
          allow='accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
          allowFullScreen
        />
      </div>
      <TextLink
        size='medium'
        weight='bold'
        onClick={() => onTextLinkClick(urls.allCaseStudies)}
        trackingId='stripe-signup-more-case-studies'
      >
        {t('See More Case Studies')}
      </TextLink>
    </div>
  );
};
